import { IBidResponse } from '../../typings/IPrebid'
import { IBidRequest } from '../../typings/IPrebidAdapter'
import { Device } from '../helpers'
import WebModel from '../models/WebModel'
import Session from '../session/Session'
import { getS2SBeta } from '../prebidFacade/s2sAdapter/BetaTest'
import { Flooring } from '../flooring/Flooring'
import { MediaType } from '../enums/MediaType'
import SlotRepository from '../slots/SlotRepository'

export const getVersionAndGroup = (
  model: WebModel,
  dataSet?: IBidRequest | IBidResponse
): {
  version?: string
  group?: string
  wrapperVersion?: string
  wrapperGroup?: string
} => {
  if (!dataSet) {
    return {}
  }
  switch (dataSet.auctionId) {
    case MediaType.banner:
      return {
        version: model.versionGroup.version,
        group: model.versionGroup.name
      }
    case MediaType.video:
      return {
        version:
          (window.MediavineVideo && window.MediavineVideo.playerVersion) ||
          'missingVideoVersion',
        group:
          (window.MediavineVideo && window.MediavineVideo.playerGroup) ||
          'missingVideoGroup',
        wrapperVersion: model.versionGroup.version,
        wrapperGroup: model.versionGroup.name
      }
    default:
      return {
        version: 'unknownAuctionId',
        group: 'unknownAuctionId'
      }
  }
}

export const getBaseData = (
  model: WebModel,
  dataSet?: IBidRequest | IBidResponse
): object => ({
  ...getVersionAndGroup(model, dataSet),
  deviceType: Device.deviceType,
  domain: window.location.hostname.replace('www.', ''),
  referrer: Session.referrer,
  sessionDepth: Session.depth,
  s2sVersion: getS2SBeta().name,
  mediaType: dataSet && dataSet.auctionId,
  // deprecated for mediaType
  // Maintained for data team
  generator: dataSet && dataSet.auctionId === MediaType.banner ? 'web' : 'video'
})

export const parseBidTimeout = (
  bidResponse: IBidResponse,
  model: WebModel
): object => ({
  ...getBaseData(model, bidResponse),
  adunit:
    bidResponse.adUnitCode &&
    bidResponse.adUnitCode.replace(/_[0-9]+|_refresh/g, ''),
  bidder: bidResponse.s2sBidder
    ? `${bidResponse.s2sBidder}_s2s`
    : bidResponse.bidder
})

export const parseBidRequest = (
  bidRequest: IBidRequest,
  model: WebModel
): object => ({
  ...getBaseData(model, bidRequest),
  bidder: bidRequest.bidderCode,
  timeout: bidRequest.timeout,
  bidFloor: model.floor[bidRequest.auctionId]
})

export const parseBidResponse = (
  bidResponse: IBidResponse,
  model: WebModel
): object => {
  const slot = SlotRepository.getSlotById(bidResponse.adUnitCode)
  return {
    ...getBaseData(model, bidResponse),
    adunit:
      bidResponse.adUnitCode &&
      bidResponse.adUnitCode.replace(/_[0-9]+|_refresh/g, ''),
    bidder: bidResponse.s2sBidder
      ? `${bidResponse.s2sBidder}_s2s`
      : bidResponse.bidder,
    adUnitFull: bidResponse.adUnitCode,
    creativeId: String(bidResponse.creativeId),
    campaignId: String(bidResponse.campaignId),
    cpm: bidResponse.cpm,
    size: bidResponse.size,
    timeToRespond: bidResponse.timeToRespond,
    bidFloor: bidResponse.isOutstream
      ? model.floor.outstream
      : model.floor[bidResponse.auctionId],
    belowFloor: bidResponse.cpm < model.floor[bidResponse.auctionId],
    isOutstream: bidResponse.isOutstream,
    refreshes: slot?.refreshes
  }
}

export const parseGoogletagEvent = (
  event:
    | googletag.events.ImpressionViewableEvent
    | googletag.events.SlotRenderEndedEvent,
  model: WebModel
) => ({
  ...getBaseData(model),
  ...parseTargeting(event.slot),
  adunit: event.slot.getAdUnitPath().replace(`/1030006/${model.adunit}_`, ''),
  eventSize: event.size
})

const parseTargeting = (slot: googletag.Slot): object => {
  try {
    // @ts-ignore
    const targeting: object = slot.getTargetingMap()
    const result = {}
    Object.keys(targeting).map((k) => (result[k] = targeting[k]))

    return result
  } catch (e) {
    return {}
  }
}
